"use client";
import { useState, useRef, useEffect } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import { apiGetAllKeywords, apiSearchResult } from "@/apis/user";
import { getBaseImageUrl, getCategoryHref, getMerchantHref } from "@/constants/hooks";
import { SearchCategories, SearchMerchant } from "@/services/dataTypes";
import { faSearch, faTimes, faStore, faTags, FontAwesomeIcon } from "@/constants/icons";

interface Props {
    companyId: string;
    mer_slug: string;
    slug_type: string;
    cat_slug: string;
    domain: string;
}

let searchTimeout: ReturnType<typeof setTimeout> | null = null;

const NavSearch = ({ companyId, mer_slug, slug_type, cat_slug, domain }: Props) => {
    const router       = useRouter();
    const pathName     = usePathname();
    const searchParams = useSearchParams();

    const [search,         setSearch]         = useState(new URLSearchParams(searchParams?.toString()).get("query") || "");
    const [tagsData,       setTagsData]        = useState<string[]>([]);
    const [categoriesData, setCategoriesData]  = useState<SearchCategories[]>([]);
    const [merchantData,   setMerchantData]    = useState<SearchMerchant[]>([]);
    const [isOpen,         setIsOpen]          = useState(false);
    const [errMsg,         setErrMsg]          = useState("");

    const inputRef    = useRef<HTMLInputElement>(null);
    const wrapperRef  = useRef<HTMLDivElement>(null);

    /* ── fetch keywords once ── */
    useEffect(() => {
        apiGetAllKeywords(companyId).then(r => {
            if (r?.data) setTagsData(r.data);
        });
    }, [companyId]);

    /* ── search on type ── */
    const handleSearch = async (q: string) => {
        if (q.trim().length < 3) return;
        const res = await apiSearchResult(q, companyId);
        const cats  = res.data?.categories || [];
        const mercs = res.data?.merchants  || [];
        if (cats.length > 0 || mercs.length > 0) {
            setCategoriesData(cats);
            setMerchantData(mercs);
            setErrMsg("");
            if (pathName !== "/search") setIsOpen(true);
        } else {
            setCategoriesData([]);
            setMerchantData([]);
            setErrMsg("No results found.");
        }
    };

    useEffect(() => {
        if (searchTimeout) clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
            if (!search.trim()) {
                setMerchantData([]); setCategoriesData([]); setErrMsg("");
            } else {
                handleSearch(search);
            }
        }, 450);
        return () => { if (searchTimeout) clearTimeout(searchTimeout); };
    }, [search]);

    /* ── open dropdown on focus if keywords exist ── */
    const handleFocus = () => {
        if (tagsData.length > 0 || merchantData.length > 0 || categoriesData.length > 0) {
            setIsOpen(true);
        }
    };

    /* ── close on outside click ── */
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (search.trim()) {
            setIsOpen(false);
            inputRef.current?.blur();
            router.push(`/search?query=${encodeURIComponent(search)}`);
        }
    };

    const clear = () => {
        setSearch(""); setMerchantData([]); setCategoriesData([]); setErrMsg("");
    };

    const hasResults = merchantData.length > 0 || categoriesData.length > 0 || tagsData.length > 0;

    return (
        <div ref={wrapperRef} className="relative w-full">

            {/* ── Input bar ── */}
            <form onSubmit={onSubmit}
                className="group flex items-center bg-white border border-slate-200 rounded-full
                           px-4 py-1.5
                           focus-within:border-[#D61C4E]/40 focus-within:shadow-[0_0_0_3px_rgba(214,28,78,0.08)]
                           transition-all duration-300">

                <FontAwesomeIcon icon={faSearch}
                    className="w-3.5 h-3.5 text-slate-300 group-focus-within:text-[#D61C4E]
                               transition-colors mr-3 shrink-0" />

                <input
                    ref={inputRef}
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onFocus={handleFocus}
                    placeholder="Search stores or deals..."
                    className="flex-1 bg-transparent outline-none text-[13px] text-slate-800
                               font-medium py-2 placeholder:text-slate-300 min-w-0"
                />

                {search && (
                    <button type="button" onClick={clear}
                        className="w-5 h-5 flex items-center justify-center rounded-full
                                   bg-slate-100 hover:bg-slate-200 transition-colors mr-2 shrink-0">
                        <FontAwesomeIcon icon={faTimes} className="w-2.5 h-2.5 text-slate-400" />
                    </button>
                )}

                <button type="submit"
                    className="bg-[#D61C4E] hover:bg-slate-900 text-white
                               px-5 py-2 rounded-full text-[11px] font-black uppercase tracking-widest
                               transition-all active:scale-95 shadow-md shadow-[#D61C4E]/20 shrink-0">
                    Search
                </button>
            </form>

            {/* ── Dropdown ── */}
            {isOpen && hasResults && (
                <div className="absolute top-[calc(100%+8px)] left-0 w-full md:w-[520px]
                                bg-white rounded-2xl border border-slate-100
                                shadow-[0_20px_60px_-8px_rgba(0,0,0,0.15)]
                                z-[999] max-h-[480px] overflow-y-auto
                                [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]
                                animate-in fade-in zoom-in-95 duration-200">

                    <div className="p-4 space-y-5">

                        {/* Merchants */}
                        {merchantData.length > 0 && (
                            <div>
                                <div className="flex items-center gap-2 mb-3 px-1">
                                    <FontAwesomeIcon icon={faStore} className="w-3 h-3 text-[#D61C4E]" />
                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.25em]">
                                        Top Merchants
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {merchantData.map((item, i) => (
                                        <Link key={i}
                                            href={getMerchantHref(item, mer_slug, slug_type)}
                                            onClick={() => setIsOpen(false)}
                                            className="group/r flex items-center gap-3 p-3 rounded-xl
                                                       border border-transparent hover:border-[#D61C4E]/15
                                                       hover:bg-[#D61C4E]/5 transition-all no-underline">
                                            <div className="w-9 h-9 relative bg-white border border-slate-100 rounded-xl
                                                            flex items-center justify-center overflow-hidden shrink-0
                                                            group-hover/r:border-[#D61C4E]/20">
                                                <Image
                                                    src={getBaseImageUrl(domain, item.merchant_logo, "")}
                                                    alt={item.merchant_name}
                                                    fill className="object-contain p-1"
                                                />
                                            </div>
                                            <span className="text-[13px] font-semibold text-slate-700
                                                             group-hover/r:text-[#D61C4E] transition-colors truncate">
                                                {item.merchant_name}
                                            </span>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Categories */}
                        {categoriesData.length > 0 && (
                            <div>
                                <div className="flex items-center gap-2 mb-3 px-1">
                                    <FontAwesomeIcon icon={faTags} className="w-3 h-3 text-[#D61C4E]" />
                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.25em]">
                                        Categories
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                    {categoriesData.map((item, i) => (
                                        <Link key={i}
                                            href={getCategoryHref(item, cat_slug, slug_type)}
                                            onClick={() => setIsOpen(false)}
                                            className="group/c flex items-center justify-between p-3 rounded-xl
                                                       border border-transparent hover:border-[#D61C4E]/15
                                                       hover:bg-[#D61C4E]/5 transition-all no-underline">
                                            <span className="text-[12px] font-semibold text-slate-700
                                                             group-hover/c:text-[#D61C4E] transition-colors">
                                                {item?.name}
                                            </span>
                                            <div className="w-5 h-5 rounded-full bg-slate-50 border border-slate-100
                                                            group-hover/c:bg-[#D61C4E] group-hover/c:border-[#D61C4E]
                                                            flex items-center justify-center transition-all">
                                                <FontAwesomeIcon icon={faSearch}
                                                    className="w-2 h-2 text-slate-300 group-hover/c:text-white transition-colors" />
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Error */}
                        {errMsg && (
                            <p className="text-center text-slate-400 text-sm font-medium py-4">{errMsg}</p>
                        )}

                        {/* Trending tags */}
                        {tagsData.length > 0 && !errMsg && (
                            <div className="border-t border-slate-50 pt-4">
                                <div className="flex items-center gap-2 mb-3 px-1">
                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.25em]">
                                        Trending
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {tagsData.slice(0, 10).map((tag, i) => (
                                        <Link key={i}
                                            href={`/search?query=${encodeURIComponent(tag)}`}
                                            onClick={() => setIsOpen(false)}
                                            className="px-3 py-1.5 rounded-full text-[11px] font-bold
                                                       bg-slate-50 border border-slate-100 text-slate-600
                                                       hover:bg-[#D61C4E] hover:text-white hover:border-[#D61C4E]
                                                       transition-all no-underline">
                                            #{tag}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NavSearch;
